import { accountsApi, workspacesApi, usersApi, embedApi, syncApi, webhooksApi } from './api'
import { applyPendingRows, useWorkspaceStore } from '../stores/workspaceStore'
import type { ApiResponse, CreateAccountRequest, CreateWorkspaceRequest, WorkspaceCreator, WorkspaceUser } from './api'
import type { Account, User, UserMembership, Workspace } from '../stores/workspaceStore'
/**
 * `refreshMembership`'s options. A stale-role refusal passes `refusedClerkUserId`: the user who
 * ISSUED the refused request, captured before it was sent — what the identity check runs on.
 * The KEY's presence marks a refusal, so a refusal whose issuing user is unknown (`undefined`)
 * refreshes nothing. No reason or workspace travels: a refusal only triggers the re-read, and
 * the re-read decides (PR #237 rounds 7–8).
 */
export type RefreshMembershipOptions =
  | { removedRowIds?: string[]; refusedClerkUserId?: never }
  | { removedRowIds?: undefined; refusedClerkUserId: string | undefined }

/**
 * Who is creating a workspace. Only the display names travel to the server: since MAR-250
 * `POST /workspaces` writes the creator's `owner` row itself, taking `clerkUserId` and email
 * from the caller's verified identity — never from the body. `clerkUserId` stays here only so
 * `createWorkspace` can refuse an unauthenticated creation before any request.
 */
export interface WorkspaceCreatorInfo extends WorkspaceCreator {
  clerkUserId?: string
}

function creatorPayload(info?: WorkspaceCreatorInfo): WorkspaceCreator | undefined {
  if (!info) return undefined
  const creator: WorkspaceCreator = {}
  if (info.firstName) creator.firstName = info.firstName
  if (info.lastName) creator.lastName = info.lastName
  if (info.name) creator.name = info.name
  return Object.keys(creator).length > 0 ? creator : undefined
}

/**
 * The store's `User` for the owner row `POST /workspaces` returned as `membership`, or `null`
 * when the response carried none (an API-key or Marva-admin creation writes no row).
 *
 * Taken straight from the create response, never from a roster read: `GET /users/workspace/{id}`
 * reads an eventually-consistent index, so a just-written row can be briefly absent, and a
 * creator left without `currentUser` saw Edit role / Remove on their own row (MAR-250 review).
 */
function userFromMembership(response: ApiResponse): User | null {
  const row: WorkspaceUser | undefined = response.membership
  if (!row) return null
  return {
    id: row.id,
    accountId: row.accountId,
    workspaceId: row.workspaceId,
    email: row.email || row.emailAddress || '',
    name: row.name || [row.firstName, row.lastName].filter(Boolean).join(' '),
    firstName: row.firstName || undefined,
    lastName: row.lastName || undefined,
    role: row.role,
    clerkUserId: row.clerkUserId || undefined,
  }
}

// Workspace service for handling business logic
export class WorkspaceService {
  private getStore = () => useWorkspaceStore.getState()

  /**
   * Put the row `POST /workspaces` wrote into `userMembership`, so the creator's role in the
   * new workspace is readable the moment it exists.
   *
   * ⚠️ This is what lets `roleForWorkspace` be the ONLY role source. `GET /users/memberships`
   * reads an eventually-consistent index and can lack the new row for a while; the old cover
   * for that window was a `currentUser.role` fallback, which also kept a departed owner's
   * stale row answering "owner" until reload (MAR-250 review round 2). The create response
   * carries the row, so it goes where every surface reads roles from instead.
   */
  private mergeCreatedMembership(
    response: ApiResponse,
    account: Account,
    workspace: Workspace,
    sessionClerkUserId: string | undefined,
    issuedInGeneration: number
  ) {
    const row: WorkspaceUser | undefined = response.membership
    if (!row || !workspace?.id) return
    // `sessionClerkUserId` is who issued the create: with no membership loaded, the store
    // seeds one from the row only when the row is that user's (PR #237 round 6).
    // `issuedInGeneration` is the session it was issued in, captured BEFORE the create's
    // awaits: if that session ended meanwhile (sign-out), the store writes nothing (round 7).
    this.getStore().mergeMembershipRow(
      account,
      { workspace, userRole: row.role, userId: row.id },
      row.clerkUserId || undefined,
      sessionClerkUserId,
      issuedInGeneration
    )
  }

  /**
   * After a removal succeeded: if the removed row was one of the CALLER's rows in that
   * workspace (they left), re-read the membership without it and return `true` — the caller
   * has lost that role and the screen must stop offering what it allowed.
   */
  async afterMemberRemoved(workspaceId: string, removedRowId: string): Promise<boolean> {
    const own = this.getStore().userMembership?.accounts.some((acc) =>
      acc.workspaces.some((e) => e.workspace?.id === workspaceId && e.userId === removedRowId)
    )
    if (!own) return false
    await this.refreshMembership({ removedRowIds: [removedRowId] })
    return true
  }

  /**
   * Re-read the caller's memberships and replace `userMembership` with them — nothing else.
   *
   * Called when the caller may have LOST a row: they left a workspace, or the server refused
   * them `no_workspace_membership` / `insufficient_workspace_role` (someone removed or demoted
   * them). Without it the UI keeps offering acts the server refuses until a reload.
   *
   * ⚠️ Deliberately not `loadWorkspaceData`, which also calls `switchAccount` and so moves the
   * merchant to the account's first workspace. A refresh must not navigate.
   *
   * `refusedClerkUserId` is the user who issued a request the server just refused with a
   * stale-role reason (`isStaleRoleRefusal`). The
   * ONE place that refusal is acted on, and it acts by RE-READING ONLY: nothing is deleted on
   * the refusal (PR #237 round 7). Neither reason is on its own evidence the cache is wrong —
   * `no_workspace_membership` is also what `assertWorkspaceRole`'s lagging `WorkspaceIndex`
   * answers for a row written seconds ago, and `insufficient_workspace_role` is also what a
   * correctly cached admin gets for an owner-only act. Stripping on them locked a real owner
   * out of the workspace she had just made, and an admin out of their own role. So the
   * `ClerkUserIndex` read below decides, with pending created rows re-applied or dropped by
   * `setUserMembership` exactly as on any other read — see `PendingMembershipRow` for what
   * that does to a demotion and to a removal.
   *
   * `removedRowIds` are rows the caller KNOWS are gone (a removal that just succeeded). The
   * memberships index is eventually consistent, so the re-read can still list them; they are
   * dropped from what it returns. A failed read leaves the membership as it was: a failure is
   * not evidence about membership.
   *
   * ⚠️ The store is re-checked after the await: if the signed-in user changed (sign-out, a
   * user switch) while the read was in flight, its answer describes someone else and is
   * dropped without touching the store.
   */
  async refreshMembership(options: RefreshMembershipOptions = {}): Promise<boolean> {
    // ⚠️ A stale-role refusal is about the user who ISSUED the request (`refusedClerkUserId`,
    // captured before it was sent). If the store now belongs to someone else — a sign-out or a
    // user switch while it was in flight — or the issuer is unknown, it says nothing about
    // whoever holds the store now: no refresh at all.
    if ('refusedClerkUserId' in options) {
      const refused = options.refusedClerkUserId
      if (!refused || this.getStore().userMembership?.clerkUserId !== refused) return false
    }
    const current = this.getStore().userMembership
    const clerkUserId = current?.clerkUserId
    if (!clerkUserId) return false
    try {
      const res = await usersApi.getMemberships(clerkUserId)
      if (!res.success || !res.data) return false
      // The read is about `clerkUserId`; a store that now belongs to someone else (or to
      // nobody) must not be written with it.
      if (this.getStore().userMembership?.clerkUserId !== clerkUserId) return false
      const removed = new Set(options.removedRowIds || [])
      if (removed.size > 0) this.getStore().dropPendingMembershipRows([...removed])
      const fresh = res.data as UserMembership
      this.getStore().setUserMembership({
        ...fresh,
        clerkUserId: fresh.clerkUserId || clerkUserId,
        // The Clerk flag is not in the memberships response; it came from publicMetadata.
        isSuperAdmin: current?.isSuperAdmin,
        accounts: (fresh.accounts || []).map((acc) => ({
          ...acc,
          workspaces: (acc.workspaces || []).filter((e) => !removed.has(e.userId)),
        })),
      })
      return true
    } catch (error) {
      console.warn('Failed to refresh memberships:', error)
      return false
    }
  }

  async createNewWorkspace(
    accountData: CreateAccountRequest,
    workspaceData: Omit<CreateWorkspaceRequest, 'accountId' | 'creator'>,
    creatorInfo?: WorkspaceCreatorInfo
  ) {
    // Captured before any await. If the session that issued this create ends while it is in
    // flight (sign-out, a user switch), nothing it returns is written to the store — persisted
    // session state included — `mergeCreatedMembership` seeds nothing (PR #237 round 7), and
    // the create resolves `null` rather than a result the caller would treat as a success.
    const generation = this.getStore().sessionGeneration
    const live = () => this.getStore().sessionGeneration === generation
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      // Step 1: Create account
      const accountResponse = await accountsApi.create(accountData)
      if (!accountResponse.success) {
        throw new Error(accountResponse.error || 'Failed to create account')
      }

      const account = accountResponse.data as any
      if (live()) this.getStore().setCurrentAccount(account)

      // Step 2: Create workspace. The server writes the creator's owner row in the same
      // transaction (MAR-250); the client no longer POSTs /users for it — that request is
      // now refused as a self-grant.
      const creator = creatorPayload(creatorInfo)
      const workspaceResponse = await workspacesApi.create({
        ...workspaceData,
        accountId: account.id,
        ...(creator ? { creator } : {}),
      })
      if (!workspaceResponse.success) {
        throw new Error(workspaceResponse.error || 'Failed to create workspace')
      }

      const workspace = workspaceResponse.data as any
      // Step 3: currentUser is the owner row the server wrote, returned with the workspace.
      const user = userFromMembership(workspaceResponse)
      if (live()) {
        this.getStore().setCurrentWorkspace(workspace)
        if (user) {
          this.getStore().setCurrentUser(user)
        }
        this.mergeCreatedMembership(workspaceResponse, account, workspace, creatorInfo?.clerkUserId, generation)
        this.getStore().setAuthenticated(true)
      }

      // The session that issued this create has ended: nothing was written, so nothing is
      // returned for the caller to treat as a success (PR #237 round 8).
      if (!live()) return null
      return {
        account,
        workspace,
        user,
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      this.getStore().setError(errorMessage)
      throw error
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async createAccount(accountData: CreateAccountRequest) {
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      const response = await accountsApi.create(accountData)
      if (!response.success) {
        throw new Error(response.error || 'Failed to create account')
      }

      const account = response.data as any
      this.getStore().setCurrentAccount(account)

      return account
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      this.getStore().setError(errorMessage)
      throw error
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async createWorkspace(
    accountId: string,
    workspaceData: Omit<CreateWorkspaceRequest, 'accountId' | 'creator'>,
    creatorInfo?: WorkspaceCreatorInfo
  ) {
    // Captured before any await. If the session that issued this create ends while it is in
    // flight (sign-out, a user switch), nothing it returns is written to the store — persisted
    // session state included — `mergeCreatedMembership` seeds nothing (PR #237 round 7), and
    // the create resolves `null` rather than a result the caller would treat as a success.
    const generation = this.getStore().sessionGeneration
    const live = () => this.getStore().sessionGeneration === generation
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      // The server writes the creator's owner row only for a signed-in (Clerk) caller, so a
      // creation without one would leave a rosterless workspace. Refuse it here, as before.
      const clerkUserId = creatorInfo?.clerkUserId || this.getStore().userMembership?.clerkUserId
      if (!clerkUserId) {
        throw new Error('User authentication required to create workspace')
      }

      // MAR-250: the owner row is written server-side, atomically with the workspace.
      const creator = creatorPayload(creatorInfo)
      const response = await workspacesApi.create({
        ...workspaceData,
        accountId,
        ...(creator ? { creator } : {}),
      })
      if (!response.success) {
        throw new Error(response.error || 'Failed to create workspace')
      }

      const workspace = response.data as any
      // The session that issued this create has ended: nothing is written, and `null` tells the
      // caller so — its success path (switch, close, onboarding step) must not run against a
      // store with no record of the workspace (PR #237 round 8).
      if (!live()) return null
      this.getStore().setCurrentWorkspace(workspace)
      // The new workspace is now current, so currentUser must be the caller's row in IT.
      const user = userFromMembership(response)
      if (user) {
        this.getStore().setCurrentUser(user)
      }
      const store = this.getStore()
      const account: Account =
        store.userMembership?.accounts.find((acc) => acc.account.id === accountId)?.account ||
        store.accounts.find((acc) => acc.id === accountId) ||
        (store.currentAccount?.id === accountId ? store.currentAccount : null) ||
        ({ id: accountId } as Account)
      this.mergeCreatedMembership(response, account, workspace, clerkUserId, generation)

      return workspace
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      this.getStore().setError(errorMessage)
      throw error
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async updateShopifyIntegration(
    workspaceId: string,
    shopifyConfig: {
      storeUrl: string
      apiKey: string
      apiSecret: string
      webhookSecret?: string
    }
  ) {
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      const response = await workspacesApi.update(workspaceId, {
        shopify: shopifyConfig,
      })

      if (!response.success) {
        throw new Error(response.error || 'Failed to update Shopify integration')
      }

      const updatedWorkspace = response.data as any
      this.getStore().setCurrentWorkspace(updatedWorkspace)

      return updatedWorkspace
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      this.getStore().setError(errorMessage)
      throw error
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async updateWorkspaceName(workspaceId: string, name: string) {
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      const response = await workspacesApi.update(workspaceId, { name })

      if (!response.success) {
        throw new Error(response.error || 'Failed to update workspace name')
      }

      const updatedWorkspace = response.data as any
      this.getStore().setCurrentWorkspace(updatedWorkspace)

      return updatedWorkspace
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      this.getStore().setError(errorMessage)
      throw error
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async testShopifyConnection(_workspaceId: string) {
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      // TODO: Implement actual connection test endpoint
      // For now, simulate a test
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      return { success: true, message: 'Connection test successful' }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Connection test failed'
      this.getStore().setError(errorMessage)
      throw error
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async syncData(workspaceId: string) {
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      const currentWorkspace = this.getStore().currentWorkspace
      const currentAccount = this.getStore().currentAccount
      
      if (!currentWorkspace || !currentAccount) {
        throw new Error('No workspace or account selected')
      }

      // Call actual sync endpoints for customers, products, orders.
      // These only enqueue the sync (the processors run async off EventBridge), so a
      // resolved promise means "accepted", not "imported". Ordering between the three is
      // enforced server-side — the shared GraphQL client waits for the shop's single bulk
      // slot rather than racing orders against products.
      const baseData = {
        accountId: currentAccount.id,
        workspaceId: workspaceId
      }

      const kinds = ['customers', 'products', 'orders'] as const
      const results = await Promise.allSettled([
        syncApi.customers({ ...baseData, type: 'customers' }),
        syncApi.products({ ...baseData, type: 'products' }),
        syncApi.orders({ ...baseData, type: 'orders' })
      ])

      const failed = kinds.filter((_, i) => results[i].status === 'rejected')

      // Never report blanket success: this used to return `success: true` even when all
      // three rejected, so a merchant whose import never started was told it worked.
      if (failed.length > 0) {
        const reasons = results
          .map((r, i) => (r.status === 'rejected' ? `${kinds[i]}: ${r.reason}` : null))
          .filter(Boolean)
        console.warn('Some sync operations failed:', reasons)
        const message = `Could not start sync for: ${failed.join(', ')}`
        this.getStore().setError(message)
        return { success: false, message, failed: [...failed] }
      }

      return { success: true, message: 'Data sync started', failed: [] }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Data sync failed'
      this.getStore().setError(errorMessage)
      throw error
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async testWebhooks(workspaceId: string) {
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      const currentWorkspace = this.getStore().currentWorkspace
      const currentAccount = this.getStore().currentAccount
      
      if (!currentWorkspace || !currentAccount) {
        throw new Error('No workspace or account selected')
      }

      // Call actual webhook setup endpoint to verify connections
      const response = await webhooksApi.setup({
        accountId: currentAccount.id,
        workspaceId: workspaceId
      })
      
      if (!response.success) {
        throw new Error(response.error || 'Webhook test failed')
      }
      
      return { 
        success: true, 
        message: 'All webhook connections tested successfully',
        results: [
          { name: 'Orders Created', status: 'Active', lastTest: 'Success' },
          { name: 'Orders Updated', status: 'Active', lastTest: 'Success' },
          { name: 'Cart Abandonment', status: 'Active', lastTest: 'Success' },
          { name: 'Customer Created', status: 'Active', lastTest: 'Success' },
          { name: 'Product Updated', status: 'Active', lastTest: 'Success' }
        ]
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Webhook test failed'
      this.getStore().setError(errorMessage)
      throw error
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async loadWorkspaceData(clerkUserId?: string, forceRefresh: boolean = false, publicMetadata?: Record<string, unknown>) {
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      // Check if user is a Marva super-admin (internal team)
      const isSuperAdmin = publicMetadata?.isMarvaAdmin === true

      if (clerkUserId) {
        // Load user memberships for Clerk user
        console.log('🔍 Loading memberships for Clerk user:', clerkUserId, { isSuperAdmin, forceRefresh })
        const membershipsResponse = await usersApi.getMemberships(clerkUserId)
        console.log('📦 Memberships response:', membershipsResponse)

        if (membershipsResponse.success) {
          const read = (membershipsResponse.data || {}) as Partial<UserMembership>
          // Add isSuperAdmin flag from Clerk publicMetadata
          const membership: UserMembership = {
            ...read,
            clerkUserId: read.clerkUserId || clerkUserId,
            accounts: read.accounts || [],
            isSuperAdmin,
          }
          console.log('✅ Membership data:', membership)

          // ⚠️ "No accounts" is judged on the read WITH this user's pending created rows, not
          // on the read alone. Straight after `POST /workspaces` the index can lack the row
          // the create just wrote; a first-account creator would otherwise be cleared back to
          // onboarding by the very re-read that followed their create (MAR-250 review round 3).
          const withPending = applyPendingRows(membership, this.getStore().pendingMembershipRows).membership
          if (withPending.accounts.length === 0) {
            console.log('👤 New user with no accounts - needs onboarding')
            // Clear any stale data — the SAME user's session, so a create in flight still lands.
            this.getStore().clearSession({ sameSession: true })
            // Set empty membership to indicate user exists but has no accounts
            this.getStore().setUserMembership({
              clerkUserId: clerkUserId,
              accounts: []
            })
          } else {
            // Re-applies the pending rows itself, and drops those the read already lists.
            this.getStore().setUserMembership(membership)
            const merged = this.getStore().userMembership as UserMembership
            // Set first account as current if none selected
            const currentAccountId = this.getStore().currentAccount?.id
            const accountIdToSet = currentAccountId || merged.accounts[0].account.id

            console.log('🔄 Setting/refreshing account:', accountIdToSet)
            // Always call switchAccount to ensure workspaces are properly populated
            this.getStore().switchAccount(accountIdToSet)

            // Verify the state
            const stateAfterSwitch = this.getStore()
            console.log('🔍 State after loading:', {
              currentAccount: !!stateAfterSwitch.currentAccount,
              currentWorkspace: !!stateAfterSwitch.currentWorkspace,
              accountId: stateAfterSwitch.currentAccount?.id,
              workspaceId: stateAfterSwitch.currentWorkspace?.id,
              workspacesCount: stateAfterSwitch.workspaces.length,
              workspaces: stateAfterSwitch.workspaces.map(w => ({ id: w.id, name: w.name }))
            })
          }
        } else {
          console.warn('❌ Failed to load memberships:', membershipsResponse.error)
          // A failed read is not evidence about membership. With a created row still pending
          // for this user, the session is kept rather than cleared to onboarding.
          const pending = applyPendingRows(
            { clerkUserId, accounts: [] },
            this.getStore().pendingMembershipRows
          ).pending.length > 0
          if (!pending) {
            // Clear session on error to ensure onboarding shows (same user, same session).
            this.getStore().clearSession({ sameSession: true })
          }
        }
      } else {
        // Fallback to loading all data (for non-Clerk users)
        const accountsResponse = await accountsApi.list()
        if (accountsResponse.success) {
          this.getStore().setAccounts(accountsResponse.data as any)
        }

        const workspacesResponse = await workspacesApi.list()
        if (workspacesResponse.success) {
          this.getStore().setWorkspaces(workspacesResponse.data as any)
        }

        const usersResponse = await usersApi.list()
        if (usersResponse.success) {
          this.getStore().setUsers(usersResponse.data as any)
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load workspace data'
      this.getStore().setError(errorMessage)
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async switchWorkspace(workspaceId: string) {
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      const workspace = this.getStore().workspaces.find(w => w.id === workspaceId)
      if (!workspace) {
        throw new Error('Workspace not found')
      }

      const account = this.getStore().accounts.find(a => a.id === workspace.accountId)
      const user = this.getStore().users.find(u => u.workspaceId === workspaceId)

      if (!account || !user) {
        throw new Error('Invalid workspace configuration')
      }

      this.getStore().setCurrentAccount(account)
      this.getStore().setCurrentWorkspace(workspace)
      this.getStore().setCurrentUser(user)
      this.getStore().setAuthenticated(true)

      return { account, workspace, user }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to switch workspace'
      this.getStore().setError(errorMessage)
      throw error
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async generateEmbedScript(accountId: string, workspaceId: string) {
    try {
      this.getStore().setLoading(true)
      this.getStore().setError(null)

      const response = await embedApi.generateScript(accountId, workspaceId)
      if (!response.success) {
        throw new Error(response.error || 'Failed to generate embed script')
      }

      return response.data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate embed script'
      this.getStore().setError(errorMessage)
      throw error
    } finally {
      this.getStore().setLoading(false)
    }
  }

  async updateWorkspaceWebhookStatus(workspaceId: string, status: 'connected' | 'disconnected') {
    try {
      const response = await workspacesApi.update(workspaceId, {
        webhooks: {
          status,
          lastChecked: new Date().toISOString()
        }
      })

      if (response.success) {
        const updatedWorkspace = response.data as any
        this.getStore().setCurrentWorkspace(updatedWorkspace)
      }
    } catch (error) {
      console.error('Failed to update webhook status:', error)
      // Don't throw error as this is a background update
    }
  }

  logout() {
    this.getStore().clearSession()
  }
}

// Export singleton instance
export const workspaceService = new WorkspaceService()